import { ITEMS_PER_PAGE } from "../../../constants";
import {
  ExportFile,
  SpendAccount,
  useSpendAccountExportLazyQuery,
  useSpendGroupsFilteredLazyQuery,
  useSpendOrganizationAccountsQuery,
} from "graphql/generated";
import { useEffect, useState } from "react";
import { SearchInput } from "shared-components/search-input";
import ShowingResults from "shared-components/showing-results";
import {
  SnapDropDown,
  SnapIcon,
  SnapLink,
  SnapPagination,
  SnapTable,
} from "suit";
import { isNullOrEmpty } from "helpers/null-or-empty";
import { HandleSortingIcon } from "helpers/sorting-icon-change";
import { useAppContexts } from "hooks/use-context";
import { SpinnerContainer } from "shared-components/spinner";
import { FormatMoney } from "helpers/format-money";
import Divider from "shared-components/divider";
import { SnapPaginationCustomEvent } from "@snap-mobile/snap-ui/dist/types/components";
import { downloadFile } from "helpers/export-csv";
import { SpendPermissions } from "types/roles-permissions";
import useModal from "hooks/use-modal";
import FloatingActionBtn from "shared-components/floating-action-btn";
import AddGroup from "shared-components/modal/groups/add-group";
import CopyGroup from "shared-components/modal/groups/copy-group";
import ArchiveGroup from "shared-components/modal/groups/archive-group";
import UnarchiveGroup from "shared-components/modal/groups/unarchive-group";

type GroupsLiteProps = {
  type: string;
};

function GroupsLite({ type }: GroupsLiteProps) {
  const { user, program, group, season } = useAppContexts();
  const { isOpen: addGroupOpen, toggle: addGroupToggle } = useModal();
  const { isOpen: copyGroupOpen, toggle: copyGroupToggle } = useModal();
  const { isOpen: archiveGroupOpen, toggle: archiveGroupToggle } = useModal();
  const { isOpen: unarchiveGroupOpen, toggle: unarchiveGroupToggle } =
    useModal();

  const canAddGroup = user?.checkSpendPermission(
    SpendPermissions.programGroupsUpdate
  );

  const [searchText, setSearchText] = useState("");
  const [selectedAccount, setSelectedAccount] = useState<SpendAccount>();
  const [orgAccounts, setOrgAccounts] = useState<SpendAccount[]>([]);
  const [accounts, setAccounts] = useState<SpendAccount[]>([]);
  const [page, setPage] = useState(0);
  const [selectedTab, setSelectedTab] = useState("");
  const [icon, setIcon] = useState<
    "ascending-solid" | "descending-solid" | "selector-solid"
  >("selector-solid");

  const { loading: loadingAccounts, data: accountData } =
    useSpendOrganizationAccountsQuery();
  const [exportReport, { loading: loadingReport, data: exportData }] =
    useSpendAccountExportLazyQuery();
  const [getGroup, { data: groupData, loading: loadingGroup }] =
    useSpendGroupsFilteredLazyQuery();

  useEffect(() => {
    if (
      !loadingAccounts &&
      accountData &&
      accountData.spendOrganizationAccounts
    ) {
      setOrgAccounts(
        accountData.spendOrganizationAccounts.accounts as SpendAccount[]
      );
    }
  }, [loadingAccounts, accountData]);

  useEffect(() => {
    if (!loadingGroup && groupData) {
      const g = groupData.spendGroupsFiltered?.groups?.at(0);
      group?.setAndStoreActiveGroup(g!);
      season?.handleSelectedSeason({ seasonsToSet: g?.seasons });
      group?.navigateGroupPage("banking");
    }
    // eslint-disable-next-line
  }, [loadingGroup, groupData]);

  useEffect(() => {
    handleGetAccounts();
    // eslint-disable-next-line
  }, [type, orgAccounts]);

  useEffect(() => {
    handleGetAccounts();
    if (!isNullOrEmpty(searchText)) {
      let tempAccounts = [...accounts];
      setAccounts(
        tempAccounts.filter((tempAccount) =>
          tempAccount.name?.toLowerCase()?.includes(searchText.toLowerCase())
        )
      );
    }
    // eslint-disable-next-line
  }, [searchText]);

  useEffect(() => {
    if (exportData && exportData.spendAccountExport) {
      handleExport(exportData.spendAccountExport);
    }
    // eslint-disable-next-line
  }, [exportData, loadingReport]);

  const handleExport = (contentToExport: ExportFile) => {
    downloadFile({
      ...contentToExport,
      fileType: "text/csv",
    });
  };

  const handleGetAccounts = () => {
    if (type === "Active") {
      setAccounts(
        orgAccounts.filter(
          (account: SpendAccount) =>
            account.type !== "main" && account.groupStatus === "active"
        ) as SpendAccount[]
      );
    } else if (type === "Archived") {
      setAccounts(
        orgAccounts.filter(
          (account: SpendAccount) =>
            account.type !== "main" && account.groupStatus !== "active"
        )
      );
    }
  };

  const handleSort = (selectedTab: string, icon: string) => {
    if (selectedTab === "Account Owner") {
      if (icon === "selector-solid" || icon === "descending-solid") {
        accounts.sort((a, b) =>
          a.name?.toLowerCase()! < b.name?.toLowerCase()!
            ? -1
            : a.name?.toLowerCase()! > b.name?.toLowerCase()!
            ? 1
            : 0
        );
      } else {
        accounts.sort((a, b) =>
          a.name?.toLowerCase()! > b.name?.toLowerCase()!
            ? -1
            : a.name?.toLowerCase()! < b.name?.toLowerCase()!
            ? 1
            : 0
        );
      }
    }
    if (selectedTab === "Available Balance") {
      if (icon === "selector-solid" || icon === "descending-solid") {
        accounts.sort((a, b) =>
          a.available! < b.available! ? -1 : a.available! > b.available! ? 1 : 0
        );
      } else {
        accounts.sort((a, b) =>
          a.available! > b.available! ? -1 : a.available! < b.available! ? 1 : 0
        );
      }
    }
  };

  const handleGroupLink = (groupId: string) => {
    getGroup({
      variables: {
        where: {
          ids: [groupId ?? ""],
          archived: type === "Archived",
        },
      },
    });
  };

  const handleModalData = (
    selectedOption: number,
    selectedAccount?: SpendAccount
  ) => {
    switch (selectedOption) {
      case 0:
        addGroupToggle();
        break;
      case 1:
        copyGroupToggle();
        break;
      case 2:
        archiveGroupToggle();
        break;
      case 3:
        unarchiveGroupToggle();
        break;
    }
    setSelectedAccount(selectedAccount);
  };

  const canArchiveGroup = user?.checkSpendPermission(
    SpendPermissions.programGroupsArchive
  );
  const canUpdateGroup = user?.checkSpendPermission(
    SpendPermissions.programGroupsUpdate
  );
  const groupActionablesList: { name: string; text: string; value: string }[] =
    [];
  const isArchived = type === "Archived";
  if (canUpdateGroup && !isArchived) {
    groupActionablesList.push({
      name: "Copy Group",
      text: "Copy Group",
      value: "1",
    });
  }
  if (canArchiveGroup && !isArchived) {
    groupActionablesList.push({
      name: "Archive Group",
      text: "Archive Group",
      value: "2",
    });
  }
  if (canArchiveGroup && isArchived) {
    groupActionablesList.push({
      name: "Unarchive Group",
      text: "Unarchive Group",
      value: "3",
    });
  }

  return (
    <div className="wrapper">
      <div className="card">
        <SearchInput setSearchValue={setSearchText} />
        <div className="flex justify-between">
          <ShowingResults
            totalNumOfResults={accounts.length}
            numOfResultsBeingDisplayed={
              accounts.length <= 10
                ? accounts.length
                : ITEMS_PER_PAGE * page + 10 >= accounts.length
                ? accounts.length
                : ITEMS_PER_PAGE * page + 10
            }
            startingNumOfResults={
              accounts.length === 0 ? 0 : ITEMS_PER_PAGE * page + 1
            }
          />
          <div
            className="flex items-center text-blue-600 font-bold lg:text-base sm:text-sm cursor-pointer"
            onClick={() =>
              exportReport({
                variables: {
                  status: type,
                },
              })
            }
          >
            <SnapIcon size="xs" className="pb-1" icon="download-solid" />
            <p className="ml-2">Export as CSV</p>
          </div>
        </div>
        <div>
          <SnapTable>
            <table className="ui celled table mt-0">
              <thead>
                <tr>
                  <th className="w-1/2 text-cell text-left pl-4">
                    Account Owner
                    <SnapIcon
                      className="cursor-pointer"
                      icon={
                        selectedTab === "Account Owner"
                          ? icon
                          : "selector-solid"
                      }
                      size="xs"
                      color="#64748B"
                      onClick={() => {
                        HandleSortingIcon(
                          "Account Owner",
                          selectedTab,
                          setSelectedTab,
                          icon,
                          setIcon
                        );
                        handleSort(
                          "Account Owner",
                          selectedTab === "Account Owner"
                            ? icon
                            : "selector-solid"
                        );
                      }}
                    />
                  </th>
                  <th className="w-1/2 text-cell text-right pr-8">
                    Available Balance
                    <SnapIcon
                      className="cursor-pointer"
                      icon={
                        selectedTab === "Available Balance"
                          ? icon
                          : "selector-solid"
                      }
                      size="xs"
                      color="#64748B"
                      onClick={() => {
                        HandleSortingIcon(
                          "Available Balance",
                          selectedTab,
                          setSelectedTab,
                          icon,
                          setIcon
                        );
                        handleSort(
                          "Available Balance",
                          selectedTab === "Available Balance"
                            ? icon
                            : "selector-solid"
                        );
                      }}
                    />
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {!loadingAccounts ? (
                  accounts
                    .slice(
                      page * ITEMS_PER_PAGE,
                      page * ITEMS_PER_PAGE + ITEMS_PER_PAGE
                    )
                    .map((item, idx) => {
                      return (
                        <tr key={item.id} className="lg:h-12">
                          <td>
                            <SnapLink
                              className="text-sm text-blue-600 cursor-pointer"
                              sr-only="srOnly"
                              onClick={() => {
                                handleGroupLink(item.groupId ?? "");
                              }}
                            >
                              {item.name}
                            </SnapLink>
                          </td>
                          <td
                            className="flex justify-between lg:table-cell text-gray-800 lg:text-right border-t-2 border-b-0 border-gray-200 text-sm"
                            data-label="Available Balance"
                          >
                            <p className="self-center">
                              {FormatMoney(item.balance || 0)}
                            </p>
                          </td>
                          <td>
                            <SnapDropDown
                              id="mobile-menu"
                              size="lg"
                              minimal
                              modal-type="drawer"
                              buttonText={item.name || ""}
                              options={groupActionablesList}
                              onSnap-dropdown-item-selected={(e) => {
                                handleModalData(Number(e.detail.value), item);
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <SpinnerContainer
                    loading={loadingAccounts}
                    inTable
                    colSpan={2}
                  />
                )}
              </tbody>
            </table>
          </SnapTable>
          <Divider />
          <SnapPagination
            itemCount={accounts.length}
            currentPage={page}
            pageSize={ITEMS_PER_PAGE}
            onSnap-pagination-page-changed={(
              event: SnapPaginationCustomEvent<number>
            ) => setPage(event.detail)}
          ></SnapPagination>
        </div>
        {canAddGroup && (
          <FloatingActionBtn
            onClick={() => handleModalData(0)}
            icon="plus-solid"
          />
        )}
        {addGroupOpen && (
          <AddGroup
            isAddGroupOpen={addGroupOpen}
            addGroupToggle={addGroupToggle}
            signupAgreement={program?.settings?.signUpAgreement}
          />
        )}
        {copyGroupOpen && (
          <CopyGroup
            copyGroupOpen={copyGroupOpen}
            copyGroupToggle={copyGroupToggle}
            selectedGroup={{ ...selectedAccount, id: selectedAccount?.groupId }}
          />
        )}
        {archiveGroupOpen && (
          <ArchiveGroup
            archiveOpen={archiveGroupOpen}
            archiveToggle={archiveGroupToggle}
            selectedGroup={{ ...selectedAccount, id: selectedAccount?.groupId }}
          />
        )}
        {unarchiveGroupOpen && (
          <UnarchiveGroup
            unarchiveOpen={unarchiveGroupOpen}
            unarchiveToggle={unarchiveGroupToggle}
            selectedGroup={{ ...selectedAccount, id: selectedAccount?.groupId }}
          />
        )}
      </div>
    </div>
  );
}

export default GroupsLite;
